/* eslint-disable prettier/prettier */
import { useCallback, useEffect, useState } from 'react';
import {
	activityTypeService,
	ActivityType,
	CreateActivityTypeRequest,
	UpdateActivityTypeRequest,
} from '../services/activityTypeService';

/**
 * Hook
 */
export const useActivityTypes = (includeInactive: boolean = false) => {
	const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Fetch list
	 */
	const fetchActivityTypes = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const res = await activityTypeService.listActivityTypes();

			if (res.success) {
				setActivityTypes(res.data || []);
			} else {
				setError(res.message || 'Failed to fetch activity types');
			}
		} catch (err: any) {
			setError(err.message || 'Something went wrong');
		} finally {
			setLoading(false);
		}
	}, [includeInactive]);

	/**
	 * Create
	 */
	const createActivityType = async (data: CreateActivityTypeRequest) => {
		try {
			setLoading(true);
			const res = await activityTypeService.createActivityType(data);

			if (res.success && res.data) {
				setActivityTypes(prev => [...prev, res.data!]);
			}

			return res;
		} catch (err: any) {
			return { success: false, message: err.message };
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Update
	 */
	const updateActivityType = async (
		id: string,
		data: UpdateActivityTypeRequest
	) => {
		try {
			setLoading(true);
			const res = await activityTypeService.updateActivityType(id, data);

			if (res.success && res.data) {
				setActivityTypes(prev =>
					prev.map(item => (item.id === id ? res.data! : item))
				);
			}

			return res;
		} catch (err: any) {
			return { success: false, message: err.message };
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Delete
	 */
	const deleteActivityType = async (id: string) => {
		try {
			setLoading(true);
			const res = await activityTypeService.deleteActivityType(id);

			if (res.success) {
				setActivityTypes(prev => prev.filter(item => item.id !== id));
			}

			return res;
		} catch (err: any) {
			return { success: false, message: err.message };
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Reorder (Optimistic Update 🔥)
	 */
	const reorderActivityTypes = async (
		items: { id: string; displayorder: number }[]
	) => {
		const previous = [...activityTypes];

		// Optimistic UI update
		const updated = [...activityTypes].map(item => {
			const found = items.find(i => i.id === item.id);
			return found ? { ...item, displayorder: found.displayorder } : item;
		});

		setActivityTypes(
			updated.sort((a, b) => a.displayorder - b.displayorder)
		);

		try {
			const res = await activityTypeService.reorderActivityTypes(items);

			if (!res.success) {
				setActivityTypes(previous); // rollback
			}

			return res;
		} catch (err: any) {
			setActivityTypes(previous); // rollback
			return { success: false, message: err.message };
		}
	};

	/**
	 * Initial load
	 */
	useEffect(() => {
		fetchActivityTypes();
	}, [fetchActivityTypes]);

	return {
		activityTypes,
		loading,
		error,

		// actions
		fetchActivityTypes,
		createActivityType,
		updateActivityType,
		deleteActivityType,
		reorderActivityTypes,
	};
};
